
import type { PropOptions } from 'vue';
import Vue from 'vue';
import type { BravadoSelectDropdownOption } from '../BravadoSelectDropdown/types';
import { ImageShape, PossibleImagePlaceholder } from '../BravadoSelectDropdown/types';
import { MAX_ITEMS } from './constants';
import type { ICompany, IDepartment, IProductType } from '~/types/community';
import BravadoComboBox from '~/components/common/BravadoComboBox/BravadoComboBox.vue';
import { guid } from '~/components/utils/core/uid';

interface IComponentData {
  search: string;
}

type IItem = (
  | IDepartment
  | IProductType
  | ICompany
  | {
      id: number;
      name: string;
      isNew?: boolean;
    }
) &
  BravadoSelectDropdownOption;

export default Vue.extend({
  name: 'MultiselectForm',
  components: {
    BravadoComboBox,
  },
  props: {
    selectedItems: {
      type: Array,
      default: () => [],
    } as PropOptions<Array<IItem>>,
    autocompleteItems: {
      type: Array,
      default: () => [],
    } as PropOptions<Array<IItem>>,
    label: {
      type: String,
      default: '',
    } as PropOptions<string>,
    placeholder: {
      type: String,
      default: '',
    } as PropOptions<string>,
    createHandler: {
      type: Function,
      default: () => null,
    } as PropOptions<(query: string) => Promise<(IDepartment | IProductType) & BravadoSelectDropdownOption>>,
    dropdownPosition: {
      type: String,
      default: 'down',
      validator: (value: string) => ['up', 'down'].includes(value),
    },
    selectedItemFulltext: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    disabled: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    creatable: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    isValid: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    isTextArea: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    actionKeyUp: {
      type: String,
      default: 'space',
    } as PropOptions<'space' | 'enter' | ''>,
    useCustomSearch: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
  },
  data(): IComponentData {
    return {
      search: '',
    };
  },
  computed: {
    formattedOptions(): Array<BravadoSelectDropdownOption> {
      const selectedNames = this.selectedItems.map(({ name }) => name);
      const filteredList = this.autocompleteItems
        .filter(({ name }) => !selectedNames.includes(name) && name.toLowerCase().includes(this.search))
        .slice(0, MAX_ITEMS);
      return this.formatList(filteredList);
    },
    formattedValue(): Array<BravadoSelectDropdownOption> {
      return this.formatList(this.selectedItems);
    },
    id() {
      return guid();
    },
    list(): Array<BravadoSelectDropdownOption> {
      if (!this.useCustomSearch) {
        return this.formattedOptions;
      }

      return this.formatList(this.autocompleteItems);
    },
  },
  methods: {
    handleQuery(term: string): void {
      this.search = term.toLowerCase();
      this.$emit('query', this.search);
    },
    handleValueUpdate(newValue: IItem) {
      this.$emit('change', newValue);
    },
    async handleCreate(item: BravadoSelectDropdownOption): Promise<void> {
      const name = item.selectDropdownTitle;
      if (!name || this.selectedItems.some(selectedItem => selectedItem.name === name)) return;

      const newItem = await this.createHandler(item.selectDropdownTitle);

      if (!newItem) return;
      this.$emit('change', this.selectedItems.concat({ ...newItem, isNew: true }));
    },
    formatList(list: Array<IItem>): Array<BravadoSelectDropdownOption> {
      return list.map(item => {
        const dropdownOption = {
          ...item,
          selectDropdownTitle: item.name,
          selectDropdownValue: item.id,
        };

        if ('logo' in item) {
          dropdownOption.selectDropdownImage = {
            src: item.logo,
            placeholder: PossibleImagePlaceholder.company,
            shape: ImageShape.rounded,
            props: {
              alt: item.name,
            },
          };
        }

        return dropdownOption;
      });
    },
  },
});
